import { ParamsFilterKey } from "Config/filterSearchParams";
import styles from "./HomeIntro.module.scss";
import {
  Button,
  Container,
  IconButton,
  Input,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessIcon from "@mui/icons-material/Business";
import ArticleIcon from "@mui/icons-material/Article";
import { useDebounce } from "Explore/Components";
import { IconPack } from "Explore/Config/Icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSuggestKeywords } from "Explore/Services/Common";
import { getSuggestSearchAuthors } from "Explore/Services/Author";
import { getSuggestSearchInstitutions } from "Explore/Services/Institution";
import { getSuggestSearchUnits } from "Explore/Services/Unit";
import { getSuggestSearchVenues } from "Explore/Services/Venue";
import useDeviceCheck from "Hooks/useDeviceCheck";

const SELECT_CHOICES = ["faculty", "institution", "unit", "venue"];

const MAP_SUGGEST_FUNC = {
  faculty: getSuggestSearchAuthors,
  institution: getSuggestSearchInstitutions,
  unit: getSuggestSearchUnits,
  venue: getSuggestSearchVenues,
  concept: getSuggestKeywords,
};

const URL_MAPPING = {
  faculty: "faculties",
  institution: "institutions",
  unit: "units",
  venue: "venues"
}

const HomeIntro = ({ onClickExplore }) => {
  const { isMobile } = useDeviceCheck();
  const [entityChoice, setEntityChoice] = useState("faculty");
  const [query, setQuery] = useState("");
  const debouncedValue = useDebounce(query);
  const navigation = useNavigate();
  const [options, setOptions] = useState({ data: [], isLoading: false });

  useEffect(() => {
    let searchController;

    if (debouncedValue.length <= 2) {
      setOptions({ data: [], isLoading: false });
    } else if (debouncedValue) {
      if (searchController) {
        searchController.abort();
      }

      const initialOption = { value: debouncedValue, name: debouncedValue };
      setOptions({ data: [initialOption], isLoading: true });
      searchController = MAP_SUGGEST_FUNC[entityChoice](
        debouncedValue,
        (res) => {
          const options_list = res.map((item) => {
            return {
              name: item.name,
              value: item.name,
            };
          });
          setOptions({
            data: [...options_list, initialOption],
            isLoading: false,
          });
        }
      );
    }

    return () => {
      if (searchController) {
        searchController.abort();
      }
    };
  }, [debouncedValue]);

  const handleSearch = (value) => {
    if (!!value) {
      const search = new URLSearchParams();
      search.append(ParamsFilterKey.QUERY, value);
      const searchUrl = search.toString();
      navigation(`/explore/${URL_MAPPING[entityChoice]}?${searchUrl}`);
    }
  };

  return (
    <div className={styles["home-intro"]}>
      <div
        className={styles["home-intro--background-image"]}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-background.webp)` }}
      />
      <Container maxWidth="lg" className={styles["home-intro__container"]}>
        <h1 className={styles["home-intro--title"]}>
          <span>Explore</span> Our World Of Academic
        </h1>

        <div className={styles["home-intro--search__wrapper"]}>
          <div className={styles["home-intro--search"]}>
            <Select
              size={isMobile ? "small" : "medium"}
              value={entityChoice}
              onChange={(e) => {
                setEntityChoice(e.target.value);
              }}
              input={<Input />}
              className={styles["home-intro--search--dropdown"]}
              style={{ width: "120px" }}
            >
              {SELECT_CHOICES.map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice[0].toUpperCase() + choice.slice(1)}
                </MenuItem>
              ))}
            </Select>
            <Input
              size={isMobile ? "small" : "medium"}
              className={styles["home-intro--search--input"]}
              placeholder="Try to search a name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" ? handleSearch(query) : null
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    size={isMobile ? "small" : "medium"}
                    onClick={() => handleSearch(query)}
                    edge="end"
                  >
                    {IconPack.outlinedSearch}
                  </IconButton>
                </InputAdornment>
              }
            />
          </div>
          {options.isLoading && (
            <LinearProgress
              className={styles["home-intro--search--suggestions--loading"]}
              color="inherit"
            />
          )}
        </div>

      </Container>
      <div className={styles["home-intro--suggestions"]}>
        {options.data.slice(0, isMobile ? 6 : 9).map((item, i) => (
          <Button
            key={i}
            className={styles["home-intro--suggestions--item"]}
            onClick={() => handleSearch(item.value)}
          >
            {item.name}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default HomeIntro;
